import React, { useMemo } from 'react';
import { Anchor, Box, capitalizeFirstLetter, Field, FieldTypes } from '@common-fe/common-fe';
import { toNumber, toString } from 'lodash';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import ROUTES from '@/common/routes';
import { Address } from '@/common/types';
import { ClaimRepayment, DestinationType, lockboxReasonTitle, LockboxStatus } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import useGetPlanAccountQuery from '@/modules/plan/queries/useGetPlanAccount.query';
import { formatCurrency, getFullAddress } from '@/utils';

import useGetClaimRepaymentCompleteStatus from './useGetClaimRepaymentCompleteStatus';

const handleRedirectToClaim = (documentId?: string) => {
  if (documentId) {
    window.open(ROUTES.CLAIMS_PROCESSOR(documentId), '_blank');
  }
};

const getStatusTitle = (lockboxStatus?: string) => capitalizeFirstLetter(lockboxStatus || '').replace('_', ' ');

const ClaimLinks = ({ claimRepayments }: { claimRepayments: ClaimRepayment[] }) => (
  <Box data-testId="claims-links">
    {claimRepayments?.map((claimRepayment, index, array) => (
      <Box key={claimRepayment?.claimId} data-testId="claim-link" direction="row">
        <Anchor
          style={{ fontWeight: 700 }}
          onClick={() => handleRedirectToClaim(claimRepayment?.claimId)}
        >
          {claimRepayment?.claimId}
        </Anchor>
        {index !== array.length - 1 && ','}
      </Box>
    ))}
  </Box>
);

const useClaimRepaymentStatusFields = () => {
  const { originalLockbox } = useLockboxDetailsStore();
  const { getClaimRepaymentCompleteStatus } = useGetClaimRepaymentCompleteStatus();

  const lockboxStatus = originalLockbox?.lockboxStatus;
  const statusTitle = getStatusTitle(lockboxStatus);
  const destinationType = originalLockbox?.remainder?.destinationType;

  const reasonTitle = useMemo(() => originalLockbox?.reason
    ? lockboxReasonTitle?.[originalLockbox?.reason]
    : '', [originalLockbox?.reason]);

  const checkDetails = originalLockbox?.checkDetails;

  const repaidAmount = useMemo(() => originalLockbox?.claimRepayments?.reduce((sum, claimRepayment) => sum + toNumber(claimRepayment?.repaymentAmount), 0), 
    [originalLockbox?.claimRepayments]);
  
  const checkDetailsAddress: Address = useMemo(() => ({
    line1: checkDetails?.addressLine1,
    line2: checkDetails?.addressLine2,
    city: checkDetails?.city,
    state: {
      name: checkDetails?.state,
    },
    zipcode: checkDetails?.zip,
  }), [checkDetails]);
  const checkDetailsFullAddress = useMemo(() => getFullAddress(checkDetailsAddress, true),
    [checkDetailsAddress]);

  const remainderAddress: Address = useMemo(() => ({
    line1: originalLockbox?.remainder?.addressLine1,
    line2: originalLockbox?.remainder?.addressLine2,
    city: originalLockbox?.remainder?.city,
    state: {
      name: originalLockbox?.remainder?.state,
    },
    zipcode: originalLockbox?.remainder?.zip,
  }), [originalLockbox?.remainder]);
  const remainderFullAddress = useMemo(() => getFullAddress(remainderAddress, true),
    [remainderAddress]);
  const accountId = originalLockbox?.remainder?.accountId;

  const { planAccount } = useGetPlanAccountQuery({ accountId });

  const fields: Field[] = useMemo(() => {
    if (lockboxStatus === LockboxStatus.APPROVED) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Node,
          name: 'claims',
          label: 'Claims',
          value: <ClaimLinks claimRepayments={originalLockbox?.claimRepayments || []} />,
        },
        {
          type: FieldTypes.Text,
          name: 'repaidAmount',
          label: 'Repaid amount',
          value: repaidAmount ? toString(formatCurrency(repaidAmount)) : EMPTY_FIELD_VALUE,
        },
      ];
    }

    if (lockboxStatus === LockboxStatus.COMPLETED) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: getStatusTitle(getClaimRepaymentCompleteStatus()?.status) || EMPTY_FIELD_VALUE,
        },
        ...!destinationType && originalLockbox?.claimRepayments?.length ? [
          {
            type: FieldTypes.Node,
            name: 'claims',
            label: 'Claims',
            value: <ClaimLinks claimRepayments={originalLockbox?.claimRepayments} />,
          },
          {
            type: FieldTypes.Text,
            name: 'repaidAmount',
            label: 'Repaid amount',
            value: repaidAmount ? toString(formatCurrency(repaidAmount)) : EMPTY_FIELD_VALUE,
          },
        ] : [],
        ...destinationType === DestinationType.CHECK && !originalLockbox?.claimRepayments?.length ? [
          {
            type: FieldTypes.Text,
            name: 'reason',
            label: 'Reason',
            value: reasonTitle || EMPTY_FIELD_VALUE,
          },
        ] : [],
        ...destinationType === DestinationType.PLAN_ACCOUNT && !originalLockbox?.claimRepayments?.length ? [
          {
            type: FieldTypes.Text,
            name: 'method',
            label: 'Method',
            value: 'Allocate to account',
          },
        ] : [],
        ...destinationType && originalLockbox?.claimRepayments?.length ? [
          {
            type: FieldTypes.Text,
            name: 'remainderManageMethod',
            label: 'Remainder manage method',
            value: destinationType ? capitalizeFirstLetter(destinationType).replace('_', ' ') :  EMPTY_FIELD_VALUE,
          },
        ] : [],
        ...destinationType === DestinationType.CHECK ? [
          {
            type: FieldTypes.Text,
            name: 'checkId',
            label: 'Check #',
            value: originalLockbox?.checkId || EMPTY_FIELD_VALUE,
          },
        ] : [],
        ...destinationType === DestinationType.PLAN_ACCOUNT ? [
          {
            type: FieldTypes.Text,
            name: 'accountName',
            label: 'Account name',
            value: planAccount?.name || EMPTY_FIELD_VALUE,
          },
        ] : [],
        ...destinationType === DestinationType.CHECK && !originalLockbox?.claimRepayments?.length ? [
          {
            type: FieldTypes.Text,
            name: 'name',
            label: 'Name',
            value: originalLockbox?.remainder?.benefitOf || EMPTY_FIELD_VALUE,
          },
          {
            type: FieldTypes.Text,
            name: 'address',
            label: 'Address',
            value: remainderFullAddress || EMPTY_FIELD_VALUE,
          },
        ] : [],
      ] as Field [];
    }

    if (lockboxStatus === LockboxStatus.PARTIALLY_APPROVED) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
            type: FieldTypes.Text,
            name: 'remainderManageMethod',
            label: 'Remainder manage method',
            value: destinationType ? capitalizeFirstLetter(destinationType).replace('_', ' ') :  EMPTY_FIELD_VALUE,
        },
        ...destinationType === DestinationType.CHECK ? [
          {
            type: FieldTypes.Text,
            name: 'checkId',
            label: 'Check ID',
            value: originalLockbox?.checkId || EMPTY_FIELD_VALUE,
          },
        ] : [],
        ...destinationType === DestinationType.PLAN_ACCOUNT ? [
          {
            type: FieldTypes.Text,
            name: 'accountName',
            label: 'Account name',
            value: planAccount?.name || EMPTY_FIELD_VALUE,
          },
        ] : [],
      ] as Field [];
    }

    if (lockboxStatus === LockboxStatus.DECLINED) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'reason',
          label: 'Reason',
          value: reasonTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'check',
          label: 'Check #',
          value: originalLockbox?.checkId || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'name',
          label: 'Name',
          value: originalLockbox?.checkDetails?.vendorName || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'address',
          label: 'Address',
          value: checkDetailsFullAddress || EMPTY_FIELD_VALUE,
        },
      ];
    }

    if (lockboxStatus === LockboxStatus.PENDING) {
      return [
        {
          type: FieldTypes.Text,
          name: 'reason',
          label: 'Reason',
          value: reasonTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'note',
          label: 'Note',
          value: originalLockbox?.notes || EMPTY_FIELD_VALUE,
        },
      ];
    }

    if (lockboxStatus === LockboxStatus.CANCELED) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'note',
          label: 'Note',
          value: originalLockbox?.notes || EMPTY_FIELD_VALUE,
        },
      ];
    }

    return [];
  }, [
    lockboxStatus,
    statusTitle,
    originalLockbox,
    checkDetailsFullAddress,
    reasonTitle,
    repaidAmount,
    destinationType,
    planAccount?.name,
    remainderFullAddress,
    getClaimRepaymentCompleteStatus,
  ]);

  return fields;
};

export default useClaimRepaymentStatusFields;
